import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { formatDateMMDDYYYY } from '../utils/dateFormat';

/**
 * Fetches all transactions from Firestore:
 * 1. people/{personId}/transactions (email-imported)
 * 2. person.stores[].transactions (manual - from people doc)
 * Merges both and returns normalized list with person/store info.
 */
export function useAllTransactions(people) {
  const [allTransactions, setAllTransactions] = useState([]);

  useEffect(() => {
    if (!people || people.length === 0) {
      setAllTransactions([]);
      return;
    }

    async function fetchTransactions() {
      const merged = [];

      // 1. From person doc (manual transactions)
      people.forEach(person => {
        (person.stores || []).forEach(store => {
          (store.transactions || []).forEach(t => {
            merged.push({
              id: t.id || `manual-${Date.now()}-${Math.random()}`,
              type: t.type || 'income',
              amount: t.amount,
              desc: t.desc || t.description || 'Transaction',
              date: formatDateMMDDYYYY(t.date) || t.date,
              createdAt: t.createdAt instanceof Date ? t.createdAt : (t.createdAt ? new Date(t.createdAt) : new Date()),
              personId: person.id,
              personName: person.name,
              storeCode: store.storeCode,
              storeName: store.name || store.storeCode,
              source: 'manual'
            });
          });
        });
      });

      // 2. From Firestore subcollection (email-imported)
      for (const person of people) {
        try {
          const txRef = collection(db, 'people', person.id, 'transactions');
          const snapshot = await getDocs(txRef);
          snapshot.docs.forEach(docSnap => {
            const d = docSnap.data();
            const dateVal = d.date?.toDate ? d.date.toDate() : (d.date ? new Date(d.date) : new Date());
            merged.push({
              id: docSnap.id,
              type: d.type || 'income',
              amount: d.amount,
              desc: d.description || 'Etsy Payment',
              date: formatDateMMDDYYYY(dateVal),
              createdAt: d.createdAt?.toDate ? d.createdAt.toDate() : new Date(),
              personId: person.id,
              personName: person.name,
              storeCode: d.storeCode,
              storeName: d.storeName || d.storeCode,
              source: 'firestore'
            });
          });
        } catch (err) {
          console.error('[useAllTransactions] Error fetching for person', person.id, err);
        }
      }

      // Sort by createdAt desc (newest first)
      merged.sort((a, b) => {
        const tA = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt).getTime();
        const tB = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt).getTime();
        return tB - tA;
      });

      setAllTransactions(merged);
    }

    fetchTransactions();
  }, [people]);

  return allTransactions;
}
